import { waitForTracking } from '../../components/Tracking/VisitorTracking';
/* eslint-disable no-undef */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Error as ErrorIcon,
  Google as GoogleIcon
} from '@mui/icons-material';

import PageTitle from '../../components/PageTitle';
import { watchState } from '../../actions';
import { sendRequest } from '../../utils/dateHelpers';
import TurnstileWidget from './TurnstileWidget';
import gitmeLogo from '../../images/logo/gitme-logo.svg';

const API_COMPANY_SIGNUP_REQUEST_URL = '/auth/company-signup/request-code';
const API_COMPANY_SIGNUP_VERIFY_URL = '/auth/company-signup/verify-code';
const API_COMPANY_SIGNUP_CHECK_EMAIL_URL = '/auth/company-signup/check-email';
const GOOGLE_COMPANY_SIGNUP_PATH = '/auth/google/company-signup';
const RESEND_CODE_COOLDOWN_SECONDS = 2 * 60;
const VERIFICATION_CODE_EXPIRY_SECONDS = 5 * 60;
const FIELD_LIMITS = {
  email: 254,
  verificationCode: 6
};

const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'yahoo.co.uk',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'mail.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'yandex.com',
  'zoho.com'
]);

const sanitizeTextInput = (value, maxLength) =>
  Array.from(String(value || '').slice(0, maxLength))
    .filter(char => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('');

const getSafeAuthMessage = (message, fallbackMessage) => {
  if (typeof message !== 'string') return fallbackMessage;

  const trimmedMessage = message.trim();
  if (!trimmedMessage || trimmedMessage.length > 160) {
    return fallbackMessage;
  }

  const safePattern =
    /invalid|expired|already|verification|captcha|turnstile|email|code|attempt|rate limit|too many|not allowed|company|corporate|domain/i;

  return safePattern.test(trimmedMessage) ? trimmedMessage : fallbackMessage;
};

const validateEmailFormat = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getEmailDomain = email => email.split('@')[1]?.toLowerCase() || '';

const isCompanyEmail = email => {
  if (!validateEmailFormat(email)) {
    return false;
  }

  const domain = getEmailDomain(email);

  if (!domain || PUBLIC_EMAIL_DOMAINS.has(domain)) {
    return false;
  }

  return domain.includes('.') && domain.split('.').every(Boolean);
};

const extractLoginToken = response => {
  if (!response || typeof response !== 'object') {
    return '';
  }

  return (
    response.token ||
    response.authToken ||
    response.loginToken ||
    response?.loginInfo?.token ||
    response?.data?.token ||
    response?.data?.loginInfo?.token ||
    ''
  );
};

const SignUpNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const turnstileRef = useRef(null);

  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSetupToken, setPasswordSetupToken] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationExpiry, setVerificationExpiry] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [existingAccount, setExistingAccount] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState('');

  const normalizedEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const signupAttribution = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const proofId = searchParams.get('proofID') || '';

    return {
      linkedinPost: searchParams.get('linkedinPost') === 'true',
      proofId
    };
  }, [location.search]);

  const validateRequestStep = () => {
    const nextErrors = {};

    if (!normalizedEmail) {
      nextErrors.email = 'Company email is required';
    } else if (!validateEmailFormat(normalizedEmail)) {
      nextErrors.email = 'Please enter a valid email address';
    } else if (normalizedEmail.length > FIELD_LIMITS.email) {
      nextErrors.email = `Email must be at most ${FIELD_LIMITS.email} characters`;
    } else if (!isCompanyEmail(normalizedEmail)) {
      nextErrors.email = 'Please use your company email address';
    }

    if (!turnstileToken) {
      nextErrors.turnstile = 'Please complete verification to continue';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const resetTurnstile = () => {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const handleEmailChange = event => {
    const nextValue = sanitizeTextInput(event.target.value, FIELD_LIMITS.email)
      .trimStart()
      .toLowerCase();

    setEmail(nextValue);
    setExistingAccount(null);
    setEmailCheckMessage('');
    setErrors(prev => ({
      ...prev,
      email: '',
      general: ''
    }));

    if (showVerification) {
      setShowVerification(false);
      setVerificationCode('');
      setResendMessage('');
      setResendCooldown(0);
      setVerificationExpiry(0);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (loading) {
      return;
    }

    if (!validateRequestStep()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setResendMessage('');

    try {
      const emailCheckResponse = await sendRequest(
        API_COMPANY_SIGNUP_CHECK_EMAIL_URL,
        'POST',
        {
          email: normalizedEmail
        }
      );

      setExistingAccount(Boolean(emailCheckResponse?.exists));
      setEmailCheckMessage(emailCheckResponse?.message || '');

      const requestCodeResponse = await sendRequest(
        API_COMPANY_SIGNUP_REQUEST_URL,
        'POST',
        {
          email: normalizedEmail,
          turnstileToken,
          linkedinPost: signupAttribution.linkedinPost,
          proofId: signupAttribution.proofId
        }
      );

      setShowVerification(true);
      setResendCooldown(
        requestCodeResponse?.resendCooldownSeconds ||
          RESEND_CODE_COOLDOWN_SECONDS
      );
      setVerificationExpiry(
        requestCodeResponse?.verificationCodeExpiresInSeconds ||
          VERIFICATION_CODE_EXPIRY_SECONDS
      );
      resetTurnstile();
    } catch (error) {
      setErrors({
        general: getSafeAuthMessage(
          error?.errorData?.message,
          'We could not send the verification code. Please try again.'
        )
      });
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async event => {
    event.preventDefault();

    if (verifyingCode) {
      return;
    }

    const normalizedCode = verificationCode.trim();

    if (verificationExpiry <= 0) {
      setErrors(prev => ({
        ...prev,
        verificationCode:
          'This verification code has expired. Please request a new one.'
      }));
      return;
    }

    if (!/^\d{6}$/.test(normalizedCode)) {
      setErrors(prev => ({
        ...prev,
        verificationCode: 'Verification code must be 6 digits'
      }));
      return;
    }

    setVerifyingCode(true);
    setErrors(prev => ({
      ...prev,
      verificationCode: '',
      general: ''
    }));

    try {
      const response = await sendRequest(
        API_COMPANY_SIGNUP_VERIFY_URL,
        'POST',
        {
          email: normalizedEmail,
          code: normalizedCode,
          linkedinPost: signupAttribution.linkedinPost,
          proofId: signupAttribution.proofId
        }
      );

      const token = response.setupToken || extractLoginToken(response);

      if (!token) {
        throw new Error('LOGIN_TOKEN_MISSING');
      }

      localStorage.removeItem('authToken');
      setPasswordSetupToken(token);
      sessionStorage.setItem('pendingPasswordSetupToken', token);
      sessionStorage.setItem('pendingPasswordSetupEmail', normalizedEmail);
      setShowVerification(false);
      setShowPasswordSetup(true);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        verificationCode: getSafeAuthMessage(
          error?.errorData?.message,
          'The code is invalid or may have expired.'
        )
      }));
    } finally {
      setVerifyingCode(false);
    }
  };

  const handlePasswordSetup = async event => {
    event.preventDefault();

    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(password)
    ) {
      setErrors(prev => ({
        ...prev,
        password:
          'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.'
      }));
      return;
    }

    if (password !== confirmPassword) {
      setErrors(prev => ({ ...prev, password: 'Passwords do not match.' }));
      return;
    }

    setLoading(true);
    setErrors(prev => ({ ...prev, password: '', general: '' }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/auth/company-signup/set-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${passwordSetupToken}`
          },
          body: JSON.stringify({ password })
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.message || 'Password could not be created.');
      }

      const loginToken = data?.token || data?.loginInfo?.token;
      if (!loginToken) {
        throw new Error('Login session could not be created.');
      }

      localStorage.setItem('authToken', loginToken);
      sessionStorage.removeItem('pendingPasswordSetupToken');
      sessionStorage.removeItem('pendingPasswordSetupEmail');
      setVerificationSuccess(true);
      dispatch(watchState());
      window.setTimeout(() => window.location.replace('/'), 800);
    } catch (error) {
      setErrors(prev => ({ ...prev, password: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLoading || resendCooldown > 0) {
      return;
    }

    if (!turnstileToken) {
      setErrors(prev => ({
        ...prev,
        turnstile: 'Please complete verification to resend the code'
      }));
      return;
    }

    setResendLoading(true);
    setErrors(prev => ({
      ...prev,
      general: '',
      verificationCode: '',
      turnstile: ''
    }));
    setResendMessage('');

    try {
      const resendResponse = await sendRequest(
        API_COMPANY_SIGNUP_REQUEST_URL,
        'POST',
        {
          email: normalizedEmail,
          turnstileToken,
          resend: true,
          linkedinPost: signupAttribution.linkedinPost,
          proofId: signupAttribution.proofId
        }
      );

      setResendMessage('A new code has been sent to your email');
      setResendCooldown(
        resendResponse?.resendCooldownSeconds || RESEND_CODE_COOLDOWN_SECONDS
      );
      setVerificationExpiry(
        resendResponse?.verificationCodeExpiresInSeconds ||
          VERIFICATION_CODE_EXPIRY_SECONDS
      );
      resetTurnstile();
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        general: getSafeAuthMessage(
          error?.errorData?.message,
          'We could not resend the code. Please try again.'
        )
      }));
      resetTurnstile();
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleContinue = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    if (!backendUrl) {
      setErrors({
        general:
          'Backend URL must be configured for Google sign-in. Please check REACT_APP_BACKEND_URL.'
      });
      return;
    }

    const googleSignupUrl = new URL(
      `${backendUrl}${GOOGLE_COMPANY_SIGNUP_PATH}`
    );

    if (signupAttribution.linkedinPost) {
      googleSignupUrl.searchParams.set('linkedinPost', 'true');
    }

    if (signupAttribution.proofId) {
      googleSignupUrl.searchParams.set('proofID', signupAttribution.proofId);
    }
    await waitForTracking();

    window.location.href = googleSignupUrl.toString();
  };

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setResendCooldown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [resendCooldown]);

  useEffect(() => {
    if (verificationExpiry <= 0) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setVerificationExpiry(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [verificationExpiry]);

  useEffect(() => {
    setExistingAccount(null);
    setEmailCheckMessage('');
  }, [normalizedEmail]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromGoogle = searchParams.get('token');
    const googleError = searchParams.get('googleError');

    if (googleError) {
      setErrors(prev => ({
        ...prev,
        general: googleError
      }));
    }

    if (!tokenFromGoogle) {
      return;
    }

    localStorage.setItem('authToken', tokenFromGoogle);
    dispatch(watchState());

    navigate('/', { replace: true });
  }, [dispatch, location.search, navigate]);

  useEffect(() => {
    const pendingToken = sessionStorage.getItem('pendingPasswordSetupToken');
    const pendingEmail = sessionStorage.getItem('pendingPasswordSetupEmail');

    if (pendingToken && pendingEmail && !localStorage.getItem('authToken')) {
      setPasswordSetupToken(pendingToken);
      setEmail(pendingEmail);
      setShowPasswordSetup(true);
    }
  }, []);

  return (
    <>
      <PageTitle>Sign Up</PageTitle>

      <div className="min-h-screen bg-[#f6f8ff] px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.08)] lg:grid-cols-[1.05fr_0.95fr]">
            <div className="hidden bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.22),_transparent_40%),linear-gradient(160deg,#0f172a_0%,#1e3a8a_52%,#38bdf8_100%)] p-10 text-white lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-100/80">
                  Corporate Access
                </p>
                <h1 className="mt-4 text-4xl font-semibold leading-tight">
                  Verify with your company email and get straight in.
                </h1>
              </div>
            </div>

            <div className="p-6 sm:p-10 lg:p-12">
              <div className="mx-auto max-w-md">
                <div className="mb-8 flex items-center justify-between">
                  <Link to="/" className="inline-flex items-center gap-3">
                    <img src={gitmeLogo} alt="Gitme" className="h-9 w-auto" />
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => {
                      sessionStorage.removeItem('pendingPasswordSetupToken');
                      sessionStorage.removeItem('pendingPasswordSetupEmail');
                    }}
                    className="text-sm font-medium text-slate-500 transition hover:text-slate-900"
                  >
                    Sign in
                  </Link>
                </div>

                <div className="mb-8">
                  <h2 className="text-3xl font-semibold tracking-tight text-slate-950">
                    {verificationSuccess
                      ? 'Signing you in'
                      : showPasswordSetup
                        ? 'Create your password'
                        : showVerification
                          ? 'Enter your email code'
                          : existingAccount
                            ? 'An account already exists'
                            : 'Continue with your company email'}
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {verificationSuccess
                      ? 'Verification completed. Redirecting you to your account.'
                      : showPasswordSetup
                        ? 'Your email is verified. Create a secure password to finish setting up your account.'
                        : showVerification
                          ? `Enter the 6-digit code sent to ${normalizedEmail}.`
                          : existingAccount
                            ? 'An account already exists for this email. Please sign in instead or use a different email.'
                            : 'Only company email addresses can be used to create a new account or continue to Gitme.'}
                  </p>
                </div>

                {errors.general ? (
                  <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <ErrorIcon className="mt-0.5 h-5 w-5 flex-shrink-0" />
                    <span>{errors.general}</span>
                  </div>
                ) : null}

                {verificationSuccess ? (
                  <div className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-6 text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg">
                      <CheckCircleIcon className="h-8 w-8" />
                    </div>
                    <p className="mt-4 text-lg font-semibold text-slate-950">
                      Email verified
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      Your session is starting. You will be redirected to the
                      dashboard in a moment.
                    </p>
                  </div>
                ) : showPasswordSetup ? (
                  <form onSubmit={handlePasswordSetup} className="space-y-5">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Create password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={event => {
                          setPassword(event.target.value);
                          setErrors(prev => ({ ...prev, password: '' }));
                        }}
                        autoComplete="new-password"
                        placeholder="Create a secure password"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-4 text-lg text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        Confirm password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={event => {
                          setConfirmPassword(event.target.value);
                          setErrors(prev => ({ ...prev, password: '' }));
                        }}
                        autoComplete="new-password"
                        placeholder="Confirm your password"
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-4 text-lg text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                      />
                    </div>
                    {errors.password ? (
                      <p className="text-sm text-red-600">{errors.password}</p>
                    ) : (
                      <p className="text-sm leading-6 text-slate-500">
                        Use at least 8 characters with uppercase, lowercase, a
                        number, and a special character.
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex w-full items-center justify-center rounded-2xl bg-[#1a73e8] px-5 py-4 text-lg font-semibold text-white transition hover:bg-[#1765c8] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {loading
                        ? 'Creating password...'
                        : 'Create password and continue'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        sessionStorage.removeItem('pendingPasswordSetupToken');
                        sessionStorage.removeItem('pendingPasswordSetupEmail');
                        setPasswordSetupToken('');
                        setPassword('');
                        setConfirmPassword('');
                        setShowPasswordSetup(false);
                        setShowVerification(false);
                      }}
                      className="w-full text-sm font-medium text-slate-500 transition hover:text-slate-900"
                    >
                      Start over with a different email
                    </button>
                  </form>
                ) : !showVerification ? (
                  <div className="space-y-5">
                    <button
                      type="button"
                      onClick={handleGoogleContinue}
                      className="flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-300 px-5 py-4 text-lg font-medium text-slate-800 transition hover:border-slate-400 hover:bg-slate-50"
                    >
                      <GoogleIcon className="text-[#4285F4]" />
                      Continue with Google
                    </button>

                    <div className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-sm font-semibold tracking-[0.32em] text-slate-400">
                        OR
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Company email address
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                            <EmailIcon className="h-5 w-5" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="name@company.com"
                            autoComplete="email"
                            className="w-full rounded-2xl border border-slate-300 bg-white py-4 pl-12 pr-4 text-lg text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                          />
                        </div>
                        {errors.email ? (
                          <p className="mt-2 text-sm text-red-600">
                            {errors.email}
                          </p>
                        ) : emailCheckMessage ? (
                          <p
                            className={`mt-2 text-sm ${
                              existingAccount
                                ? 'font-medium text-red-600'
                                : 'text-emerald-600'
                            }`}
                          >
                            {emailCheckMessage}
                          </p>
                        ) : (
                          <p className="mt-2 text-sm text-slate-500">
                            Examples: `@company.com`, `@startup.io`,
                            `@brand.co.uk`
                          </p>
                        )}
                      </div>

                      <div>
                        <TurnstileWidget
                          ref={turnstileRef}
                          onSuccess={setTurnstileToken}
                        />
                        {errors.turnstile ? (
                          <p className="mt-2 text-sm text-red-600">
                            {errors.turnstile}
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1a73e8] px-5 py-4 text-lg font-semibold text-white transition hover:bg-[#1765c8] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {loading ? 'Sending code...' : 'Continue'}
                        <ArrowForwardIcon className="h-5 w-5" />
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <form onSubmit={handleVerifyCode} className="space-y-5">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Verification code
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          value={verificationCode}
                          onChange={event => {
                            setVerificationCode(
                              event.target.value.replace(/\D/g, '').slice(0, 6)
                            );
                            setErrors(prev => ({
                              ...prev,
                              verificationCode: '',
                              general: ''
                            }));
                          }}
                          placeholder="6-digit code"
                          className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-4 text-center text-2xl tracking-[0.5em] text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        />
                        {errors.verificationCode ? (
                          <p className="mt-2 text-sm text-red-600">
                            {errors.verificationCode}
                          </p>
                        ) : null}
                      </div>

                      <button
                        type="submit"
                        disabled={verifyingCode}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1a73e8] px-5 py-4 text-lg font-semibold text-white transition hover:bg-[#1765c8] disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {verifyingCode ? 'Verifying...' : 'Verify and sign in'}
                      </button>
                    </form>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-600">
                        Did not receive the code? Complete verification before
                        requesting a new one.
                      </p>

                      <div className="mt-4">
                        <TurnstileWidget
                          ref={turnstileRef}
                          onSuccess={setTurnstileToken}
                        />
                        {errors.turnstile ? (
                          <p className="mt-2 text-sm text-red-600">
                            {errors.turnstile}
                          </p>
                        ) : null}
                      </div>

                      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={resendLoading || resendCooldown > 0}
                          className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {resendLoading
                            ? 'Sending...'
                            : resendCooldown > 0
                              ? `Resend (${resendCooldown}s)`
                              : 'Resend code'}
                        </button>

                        <span className="text-sm text-slate-500">
                          {verificationExpiry > 0
                            ? `Code expires in ${Math.ceil(verificationExpiry / 60)} min`
                            : 'Code expired'}
                        </span>

                        <button
                          type="button"
                          onClick={() => {
                            setShowVerification(false);
                            setVerificationCode('');
                            setErrors({});
                            setResendMessage('');
                            setResendCooldown(0);
                            setVerificationExpiry(0);
                            resetTurnstile();
                          }}
                          className="text-sm font-medium text-slate-500 transition hover:text-slate-900"
                        >
                          Change email address
                        </button>
                      </div>

                      {resendMessage ? (
                        <p className="mt-3 text-sm font-medium text-emerald-600">
                          {resendMessage}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpNew;
